import React from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  FileText, 
  IndianRupee, 
  TrendingUp,
  Clock,
  CheckCircle2,
  XCircle,
  Plus,
  Download,
  Search,
  RefreshCw,
  Loader2,
  Eye,
  Printer,
  Filter,
  Calendar,
  Upload,
  FileSpreadsheet,
  Truck,
  Trash2,
  Pencil,
  Lock
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import BulkInvoiceUpload from '@/components/BulkInvoiceUpload';
import EwayBillDialog from '@/components/EwayBillDialog';
import { useModulePermissions } from '@/hooks/useModulePermissions';

var API = process.env.REACT_APP_BACKEND_URL + '/api';

function InvoiceDashboard() {
  var modulePerms = useModulePermissions();
  var canCreateInvoice = modulePerms.canCreate('invoices');
  var canEditInvoice = modulePerms.canEdit('invoices');
  var canDeleteInvoice = modulePerms.canDelete('invoices');
  var loadingState = React.useState(true);
  var loading = loadingState[0];
  var setLoading = loadingState[1];
  
  var dashboardState = React.useState(null);
  var dashboard = dashboardState[0];
  var setDashboard = dashboardState[1];
  
  var invoicesState = React.useState([]);
  var invoices = invoicesState[0];
  var setInvoices = invoicesState[1];
  
  var searchState = React.useState('');
  var search = searchState[0];
  var setSearch = searchState[1];
  
  var statusFilterState = React.useState('');
  var statusFilter = statusFilterState[0];
  var setStatusFilter = statusFilterState[1];
  
  var fromDateState = React.useState('');
  var fromDate = fromDateState[0];
  var setFromDate = fromDateState[1];
  
  var toDateState = React.useState('');
  var toDate = toDateState[0];
  var setToDate = toDateState[1];
  
  var selectedInvoiceState = React.useState(null);
  var selectedInvoice = selectedInvoiceState[0];
  var setSelectedInvoice = selectedInvoiceState[1];
  
  var viewDialogState = React.useState(false);
  var viewDialogOpen = viewDialogState[0];
  var setViewDialogOpen = viewDialogState[1];
  
  var activeTabState = React.useState('dashboard');
  var activeTab = activeTabState[0];
  var setActiveTab = activeTabState[1];

  var ewayDialogState = React.useState(false);
  var ewayDialogOpen = ewayDialogState[0];
  var setEwayDialogOpen = ewayDialogState[1];

  // Selection state for bulk delete
  var selectedInvsState = React.useState([]);
  var selectedInvs = selectedInvsState[0];
  var setSelectedInvs = selectedInvsState[1];

  function toggleInvSelection(id) {
    setSelectedInvs(function(prev) { return prev.indexOf(id) >= 0 ? prev.filter(function(x) { return x !== id; }) : prev.concat(id); });
  }
  function toggleInvSelectAll() {
    setSelectedInvs(selectedInvs.length === invoices.length ? [] : invoices.map(function(i) { return i.id; }));
  }
  function handleDeleteSelectedInvoices() {
    if (selectedInvs.length === 0) return;
    if (!window.confirm('Delete ' + selectedInvs.length + ' invoice(s)? Only draft invoices can be deleted — finalized invoices are locked and will be skipped.')) return;
    var token = localStorage.getItem('token');
    axios.post(API + '/invoices/bulk-delete', { ids: selectedInvs }, { headers: { Authorization: 'Bearer ' + token } })
      .then(function() { toast.success(selectedInvs.length + ' invoices deleted'); setSelectedInvs([]); fetchDashboard(); fetchInvoices(); })
      .catch(function(err) { toast.error(err.response && err.response.data ? err.response.data.detail : 'Delete failed'); });
  }
  function handleDeleteSingleInvoice(id) {
    if (!window.confirm('Delete this invoice?')) return;
    var token = localStorage.getItem('token');
    axios.delete(API + '/invoices/' + id, { headers: { Authorization: 'Bearer ' + token } })
      .then(function() { toast.success('Invoice deleted'); fetchDashboard(); fetchInvoices(); })
      .catch(function(err) { toast.error(err.response && err.response.data ? err.response.data.detail : 'Delete failed'); });
  }

  var ewayInvoiceState = React.useState(null);
  var ewayInvoice = ewayInvoiceState[0];
  var setEwayInvoice = ewayInvoiceState[1];

  var ewayAutoPromptState = React.useState(false);
  var ewayAutoPrompt = ewayAutoPromptState[0];
  var setEwayAutoPrompt = ewayAutoPromptState[1];

  React.useEffect(function() {
    fetchDashboard();
    fetchInvoices();
    // Initial load only — filters trigger fetchInvoices via handleSearch button
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function fetchDashboard() {
    var token = localStorage.getItem('token');
    axios.get(API + '/invoices/dashboard', {
      headers: { Authorization: 'Bearer ' + token }
    }).then(function(response) {
      setDashboard(response.data);
    }).catch(function(error) {
      toast.error('Failed to load dashboard');
    });
  }

  function fetchInvoices() {
    setLoading(true);
    var token = localStorage.getItem('token');
    var params = new URLSearchParams();
    if (statusFilter) params.append('status', statusFilter);
    if (fromDate) params.append('from_date', fromDate);
    if (toDate) params.append('to_date', toDate);
    if (search) params.append('search', search);
    
    axios.get(API + '/invoices?' + params.toString(), {
      headers: { Authorization: 'Bearer ' + token }
    }).then(function(response) {
      setInvoices(response.data || []);
      setLoading(false);
    }).catch(function(error) {
      toast.error('Failed to load invoices');
      setLoading(false);
    });
  }

  function handleSearch() {
    fetchInvoices();
  }

  function viewInvoice(invoice) {
    setSelectedInvoice(invoice);
    setViewDialogOpen(true);
  }

  function downloadPDF(invoiceId, invoiceNumber) {
    var token = localStorage.getItem('token');
    axios.get(API + '/invoices/' + invoiceId + '/pdf', {
      headers: { Authorization: 'Bearer ' + token },
      responseType: 'blob'
    }).then(function(response) {
      var url = window.URL.createObjectURL(new Blob([response.data]));
      var link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', (invoiceNumber || invoiceId) + '.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF downloaded');
    }).catch(function(error) {
      toast.error('Failed to download PDF');
    });
  }

  function finalizeInvoice(invoiceId) {
    var token = localStorage.getItem('token');
    axios.post(API + '/invoices/' + invoiceId + '/finalize', {}, {
      headers: { Authorization: 'Bearer ' + token }
    }).then(function(response) {
      toast.success('Invoice finalized. Stock deducted.');
      fetchInvoices();
      fetchDashboard();
      setViewDialogOpen(false);
      if (response.data.eway_bill_required) {
        var inv = invoices.find(function(i) { return i.id === invoiceId; });
        var invoiceForEway = Object.assign({}, inv || {}, { 
          grand_total: response.data.grand_total,
          status: 'finalized'
        });
        setEwayInvoice(invoiceForEway);
        setEwayAutoPrompt(true);
        setEwayDialogOpen(true);
      }
    }).catch(function(error) {
      toast.error(error.response?.data?.detail || 'Failed to finalize');
    });
  }

  function openEwayBill(invoice) {
    setEwayInvoice(invoice);
    setEwayAutoPrompt(false);
    setEwayDialogOpen(true);
  }


  function cancelInvoice(invoiceId) {
    if (!confirm('Are you sure you want to cancel this invoice?')) return;
    var token = localStorage.getItem('token');
    axios.post(API + '/invoices/' + invoiceId + '/cancel', {}, {
      headers: { Authorization: 'Bearer ' + token }
    }).then(function(response) {
      toast.success('Invoice cancelled. Stock restored.');
      fetchInvoices();
      fetchDashboard();
      setViewDialogOpen(false);
    }).catch(function(error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel');
    });
  }

  function formatCurrency(val) {
    return '₹' + (val || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });
  }

  function getStatusBadge(status) {
    var config = {
      draft: { label: 'Draft', className: 'bg-slate-100 text-slate-800' },
      finalized: { label: 'Finalized', className: 'bg-blue-100 text-blue-800' },
      paid: { label: 'Paid', className: 'bg-green-100 text-green-800' },
      partially_paid: { label: 'Partial', className: 'bg-amber-100 text-amber-800' },
      cancelled: { label: 'Cancelled', className: 'bg-red-100 text-red-800' }
    };
    var c = config[status] || { label: status, className: 'bg-slate-100 text-slate-800' };
    return React.createElement(Badge, { className: c.className }, c.label);
  }

  var today = dashboard?.today || {};
  var summary = dashboard?.summary || {};

  return React.createElement('div', { className: 'space-y-6', 'data-testid': 'invoice-dashboard' },
    // Header
    React.createElement('div', { className: 'flex items-center justify-between flex-wrap gap-4' },
      React.createElement('div', null,
        React.createElement('h1', { className: 'text-3xl font-heading font-bold text-primary' }, 'Invoice Management'),
        React.createElement('p', { className: 'text-slate-600 mt-1' }, 'GST-compliant sales billing and tracking')
      ),
      React.createElement('div', { className: 'flex gap-2' },
        canCreateInvoice && React.createElement(Button, { 
          onClick: function() { window.location.href = '/invoices/create'; },
          className: 'gap-2 bg-green-600 hover:bg-green-700',
          'data-testid': 'create-invoice-btn'
        },
          React.createElement(Plus, { className: 'w-4 h-4' }),
          'Create Invoice'
        ),
        React.createElement(Button, { variant: 'outline', onClick: function() { window.location.href = '/delivery-challans'; }, className: 'gap-2 border-indigo-300 text-indigo-700 hover:bg-indigo-50', 'data-testid': 'create-dc-btn' },
          React.createElement(Truck, { className: 'w-4 h-4' }),
          'Create DC'
        ),
        React.createElement(Button, { variant: 'outline', onClick: function() { fetchDashboard(); fetchInvoices(); }, className: 'gap-2' },
          React.createElement(RefreshCw, { className: 'w-4 h-4' }),
          'Refresh'
        ),
        selectedInvs.length > 0 && React.createElement(Button, { variant: 'outline', onClick: handleDeleteSelectedInvoices, className: 'gap-2 border-red-300 text-red-600 hover:bg-red-50', 'data-testid': 'delete-selected-invoices' },
          React.createElement(Trash2, { className: 'w-4 h-4' }),
          'Delete Selected (' + selectedInvs.length + ')'
        )
      )
    ),
    
    // Tabs for Dashboard / Bulk Upload
    React.createElement(Tabs, { value: activeTab, onValueChange: setActiveTab },
      React.createElement(TabsList, { className: 'bg-slate-100' },
        React.createElement(TabsTrigger, { value: 'dashboard', className: 'gap-2' },
          React.createElement(FileText, { className: 'w-4 h-4' }),
          'Invoices'
        ),
        React.createElement(TabsTrigger, { value: 'bulk-upload', className: 'gap-2', 'data-testid': 'bulk-upload-tab' },
          React.createElement(Upload, { className: 'w-4 h-4' }),
          'Bulk Upload'
        )
      ),
      
      // Dashboard Tab Content
      React.createElement(TabsContent, { value: 'dashboard', className: 'space-y-6 mt-4' },
    React.createElement('div', { className: 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4' },
      // Today's Invoices
      React.createElement(Card, { className: 'bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200' },
        React.createElement(CardContent, { className: 'p-4' },
          React.createElement('div', { className: 'flex items-center justify-between' },
            React.createElement('div', null,
              React.createElement('p', { className: 'text-sm text-blue-600 font-medium' }, "Today's Invoices"),
              React.createElement('p', { className: 'text-3xl font-bold text-blue-800' }, today.count || 0),
              React.createElement('p', { className: 'text-xs text-blue-600 mt-1' }, formatCurrency(today.grand_total) + ' total')
            ),
            React.createElement(FileText, { className: 'w-10 h-10 text-blue-500 opacity-70' })
          )
        )
      ),
      
      // Today's GST
      React.createElement(Card, { className: 'bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200' },
        React.createElement(CardContent, { className: 'p-4' },
          React.createElement('div', { className: 'flex items-center justify-between' },
            React.createElement('div', null,
              React.createElement('p', { className: 'text-sm text-purple-600 font-medium' }, 'GST Collected Today'),
              React.createElement('p', { className: 'text-3xl font-bold text-purple-800' }, formatCurrency(today.total_gst)),
              React.createElement('p', { className: 'text-xs text-purple-600 mt-1' }, 'Subtotal: ' + formatCurrency(today.subtotal))
            ),
            React.createElement(IndianRupee, { className: 'w-10 h-10 text-purple-500 opacity-70' })
          )
        )
      ),
      
      // Pending Payments
      React.createElement(Card, { className: 'bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200' },
        React.createElement(CardContent, { className: 'p-4' },
          React.createElement('div', { className: 'flex items-center justify-between' },
            React.createElement('div', null,
              React.createElement('p', { className: 'text-sm text-amber-600 font-medium' }, 'Pending Payments'),
              React.createElement('p', { className: 'text-3xl font-bold text-amber-800' }, summary.pending_count || 0),
              React.createElement('p', { className: 'text-xs text-amber-600 mt-1' }, formatCurrency(summary.pending_amount) + ' due')
            ),
            React.createElement(Clock, { className: 'w-10 h-10 text-amber-500 opacity-70' })
          )
        )
      ),
      
      // Paid Invoices
      React.createElement(Card, { className: 'bg-gradient-to-br from-green-50 to-green-100 border-green-200' },
        React.createElement(CardContent, { className: 'p-4' },
          React.createElement('div', { className: 'flex items-center justify-between' },
            React.createElement('div', null,
              React.createElement('p', { className: 'text-sm text-green-600 font-medium' }, 'Paid Invoices'),
              React.createElement('p', { className: 'text-3xl font-bold text-green-800' }, summary.paid_count || 0),
              React.createElement('p', { className: 'text-xs text-green-600 mt-1' }, formatCurrency(summary.paid_amount) + ' received')
            ),
            React.createElement(CheckCircle2, { className: 'w-10 h-10 text-green-500 opacity-70' })
          )
        )
      )
    ),

    // Filters
    React.createElement(Card, null,
      React.createElement(CardContent, { className: 'p-4' },
        React.createElement('div', { className: 'flex flex-wrap gap-4 items-end' },
          React.createElement('div', { className: 'flex-1 min-w-[200px]' },
            React.createElement('label', { className: 'text-sm font-medium text-slate-600 mb-1 block' }, 'Search'),
            React.createElement('div', { className: 'relative' },
              React.createElement(Search, { className: 'absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4' }),
              React.createElement(Input, {
                placeholder: 'Invoice no, customer, GSTIN...',
                value: search,
                onChange: function(e) { setSearch(e.target.value); },
                onKeyDown: function(e) { if (e.key === 'Enter') handleSearch(); },
                className: 'pl-9'
              })
            )
          ),
          React.createElement('div', { className: 'w-32' },
            React.createElement('label', { className: 'text-sm font-medium text-slate-600 mb-1 block' }, 'Status'),
            React.createElement('select', {
              value: statusFilter,
              onChange: function(e) { setStatusFilter(e.target.value); },
              className: 'w-full h-10 px-3 border rounded-md text-sm'
            },
              React.createElement('option', { value: '' }, 'All'),
              React.createElement('option', { value: 'draft' }, 'Draft'),
              React.createElement('option', { value: 'finalized' }, 'Finalized'),
              React.createElement('option', { value: 'paid' }, 'Paid'),
              React.createElement('option', { value: 'partially_paid' }, 'Partial'),
              React.createElement('option', { value: 'cancelled' }, 'Cancelled')
            )
          ),
          React.createElement('div', { className: 'w-36' },
            React.createElement('label', { className: 'text-sm font-medium text-slate-600 mb-1 block' }, 'From Date'),
            React.createElement(Input, {
              type: 'date',
              value: fromDate,
              onChange: function(e) { setFromDate(e.target.value); }
            })
          ),
          React.createElement('div', { className: 'w-36' },
            React.createElement('label', { className: 'text-sm font-medium text-slate-600 mb-1 block' }, 'To Date'),
            React.createElement(Input, {
              type: 'date',
              value: toDate,
              onChange: function(e) { setToDate(e.target.value); }
            })
          ),
          React.createElement(Button, { onClick: handleSearch, className: 'gap-2' },
            React.createElement(Filter, { className: 'w-4 h-4' }),
            'Apply'
          )
        )
      )
    ),

    // Invoices Table
    React.createElement(Card, null,
      React.createElement(CardHeader, null,
        React.createElement(CardTitle, null, 'Invoices (' + invoices.length + ')')
      ),
      React.createElement(CardContent, null,
        loading ? 
          React.createElement('div', { className: 'flex justify-center py-8' },
            React.createElement(Loader2, { className: 'w-8 h-8 animate-spin text-blue-600' })
          ) :
          invoices.length === 0 ?
            React.createElement('div', { className: 'text-center py-8 text-slate-500' },
              React.createElement(FileText, { className: 'w-12 h-12 mx-auto mb-2 opacity-50' }),
              'No invoices found'
            ) :
            React.createElement('div', { className: 'overflow-x-auto' },
              React.createElement('table', { className: 'w-full text-sm' },
                React.createElement('thead', null,
                  React.createElement('tr', { className: 'border-b bg-slate-50' },
                    React.createElement('th', { className: 'p-3 w-10' },
                      React.createElement('input', { type: 'checkbox', checked: selectedInvs.length === invoices.length && invoices.length > 0, onChange: toggleInvSelectAll, className: 'w-4 h-4 accent-blue-600' })),
                    React.createElement('th', { className: 'text-left p-3 font-semibold' }, 'Invoice No'),
                    React.createElement('th', { className: 'text-left p-3 font-semibold' }, 'Date'),
                    React.createElement('th', { className: 'text-left p-3 font-semibold' }, 'Customer'),
                    React.createElement('th', { className: 'text-right p-3 font-semibold' }, 'Subtotal'),
                    React.createElement('th', { className: 'text-right p-3 font-semibold' }, 'GST'),
                    React.createElement('th', { className: 'text-right p-3 font-semibold' }, 'Grand Total'),
                    React.createElement('th', { className: 'text-right p-3 font-semibold' }, 'Balance'),
                    React.createElement('th', { className: 'text-center p-3 font-semibold' }, 'Status'),
                    React.createElement('th', { className: 'text-center p-3 font-semibold' }, 'Actions')
                  )
                ),
                React.createElement('tbody', null,
                  invoices.map(function(inv) {
                    var isInvSelected = selectedInvs.indexOf(inv.id) >= 0;
                    return React.createElement('tr', { key: inv.id, className: 'border-b hover:bg-slate-50 ' + (isInvSelected ? 'bg-blue-50' : '') },
                      React.createElement('td', { className: 'p-3 text-center' },
                        React.createElement('input', { type: 'checkbox', checked: isInvSelected, onChange: function() { toggleInvSelection(inv.id); }, className: 'w-4 h-4 accent-blue-600' })),
                      React.createElement('td', { className: 'p-3 font-mono font-medium text-blue-600' }, inv.invoice_number),
                      React.createElement('td', { className: 'p-3' }, inv.invoice_date),
                      React.createElement('td', { className: 'p-3' },
                        React.createElement('p', { className: 'font-medium' }, inv.customer_name),
                        inv.customer_gstin && React.createElement('p', { className: 'text-xs text-slate-500' }, inv.customer_gstin),
                        inv.created_by_name && React.createElement('p', { className: 'text-xs text-slate-400 mt-0.5', 'data-testid': 'inv-created-by-' + inv.id }, 'By: ' + inv.created_by_name)
                      ),
                      React.createElement('td', { className: 'p-3 text-right' }, formatCurrency(inv.subtotal)),
                      React.createElement('td', { className: 'p-3 text-right' }, formatCurrency(inv.total_gst)),
                      React.createElement('td', { className: 'p-3 text-right font-semibold' }, formatCurrency(inv.grand_total)),
                      React.createElement('td', { className: 'p-3 text-right ' + (inv.balance_due > 0 ? 'text-amber-600 font-semibold' : 'text-green-600') }, 
                        formatCurrency(inv.balance_due)
                      ),
                      React.createElement('td', { className: 'p-3 text-center' },
                        getStatusBadge(inv.status),
                        inv.eway_bill_number && React.createElement(Badge, { 
                          className: 'ml-1 text-xs ' + (inv.eway_bill_status === 'active' ? 'bg-green-100 text-green-800' : 'bg-slate-100 text-slate-600'),
                          'data-testid': 'eway-badge-' + inv.id
                        }, 'EWB')
                      ),
                      React.createElement('td', { className: 'p-3 text-center' },
                        React.createElement('div', { className: 'flex justify-center gap-1' },
                          React.createElement(Button, { variant: 'ghost', size: 'sm', onClick: function() { viewInvoice(inv); } },
                            React.createElement(Eye, { className: 'w-4 h-4' })
                          ),
                          inv.status === 'draft' && React.createElement(Button, {
                            variant: 'ghost', size: 'sm',
                            onClick: function() { window.location.href = '/invoices/create?editId=' + inv.id; },
                            className: 'text-blue-600 hover:text-blue-700 hover:bg-blue-50',
                            title: 'Edit Invoice',
                            'data-testid': 'edit-invoice-' + inv.id
                          },
                            React.createElement(Pencil, { className: 'w-4 h-4' })
                          ),
                          React.createElement(Button, { variant: 'ghost', size: 'sm', onClick: function() { downloadPDF(inv.id, inv.invoice_number); } },
                            React.createElement(Download, { className: 'w-4 h-4' })
                          ),
                          inv.status === 'draft' && React.createElement(Button, { variant: 'ghost', size: 'sm', onClick: function() { handleDeleteSingleInvoice(inv.id); }, className: 'text-red-500 hover:text-red-700 hover:bg-red-50', 'data-testid': 'delete-invoice-' + inv.id },
                            React.createElement(Trash2, { className: 'w-4 h-4' })
                          ),
                          inv.status !== 'draft' && React.createElement('span', {
                            className: 'inline-flex items-center gap-1 text-[10px] font-semibold text-slate-400 px-1',
                            title: 'Finalized (Locked) — editing and deletion are disabled',
                            'data-testid': 'locked-invoice-' + inv.id
                          },
                            React.createElement(Lock, { className: 'w-3.5 h-3.5' }), 'Locked'
                          )
                        )
                      )
                    );
                  })
                )
              )
            )
      )
    ),

    // View Invoice Dialog
    React.createElement(Dialog, { open: viewDialogOpen, onOpenChange: setViewDialogOpen },
      React.createElement(DialogContent, { className: 'max-w-4xl max-h-[90vh] overflow-y-auto' },
        React.createElement(DialogHeader, null,
          React.createElement(DialogTitle, null, 'Invoice Details')
        ),
        selectedInvoice && React.createElement('div', { className: 'space-y-4' },
          // Invoice Header
          React.createElement('div', { className: 'flex justify-between items-start p-4 bg-slate-50 rounded-lg' },
            React.createElement('div', null,
              React.createElement('h3', { className: 'text-xl font-bold text-blue-800' }, selectedInvoice.invoice_number),
              React.createElement('p', { className: 'text-slate-600' }, 'Date: ' + selectedInvoice.invoice_date),
              React.createElement('div', { className: 'mt-2' }, getStatusBadge(selectedInvoice.status))
            ),
            React.createElement('div', { className: 'text-right' },
              React.createElement('p', { className: 'font-semibold' }, selectedInvoice.customer_name),
              React.createElement('p', { className: 'text-sm text-slate-600' }, selectedInvoice.customer_address),
              selectedInvoice.customer_gstin && React.createElement('p', { className: 'text-sm font-mono' }, 'GSTIN: ' + selectedInvoice.customer_gstin)
            )
          ),
          
          // Items Table
          React.createElement('div', { className: 'border rounded-lg overflow-hidden' },
            React.createElement('table', { className: 'w-full text-sm' },
              React.createElement('thead', { className: 'bg-slate-100' },
                React.createElement('tr', null,
                  React.createElement('th', { className: 'text-left p-2' }, 'Product'),
                  React.createElement('th', { className: 'text-center p-2' }, 'HSN'),
                  React.createElement('th', { className: 'text-center p-2' }, 'Qty'),
                  React.createElement('th', { className: 'text-right p-2' }, 'Rate'),
                  React.createElement('th', { className: 'text-right p-2' }, 'Taxable'),
                  React.createElement('th', { className: 'text-right p-2' }, 'CGST'),
                  React.createElement('th', { className: 'text-right p-2' }, 'SGST'),
                  React.createElement('th', { className: 'text-right p-2' }, 'IGST'),
                  React.createElement('th', { className: 'text-right p-2' }, 'Total')
                )
              ),
              React.createElement('tbody', null,
                (selectedInvoice.items || []).map(function(item, idx) {
                  return React.createElement('tr', { key: idx, className: 'border-t' },
                    React.createElement('td', { className: 'p-2' }, item.product_name),
                    React.createElement('td', { className: 'p-2 text-center text-xs' }, item.hsn_code),
                    React.createElement('td', { className: 'p-2 text-center' }, item.quantity + ' ' + item.unit),
                    React.createElement('td', { className: 'p-2 text-right' }, formatCurrency(item.unit_price)),
                    React.createElement('td', { className: 'p-2 text-right' }, formatCurrency(item.taxable_amount)),
                    React.createElement('td', { className: 'p-2 text-right' }, formatCurrency(item.cgst_amount)),
                    React.createElement('td', { className: 'p-2 text-right' }, formatCurrency(item.sgst_amount)),
                    React.createElement('td', { className: 'p-2 text-right' }, formatCurrency(item.igst_amount)),
                    React.createElement('td', { className: 'p-2 text-right font-semibold' }, formatCurrency(item.total_amount))
                  );
                })
              )
            )
          ),
          
          // Summary
          React.createElement('div', { className: 'flex justify-end' },
            React.createElement('div', { className: 'w-64 space-y-1 text-sm' },
              React.createElement('div', { className: 'flex justify-between' },
                React.createElement('span', null, 'Subtotal:'),
                React.createElement('span', null, formatCurrency(selectedInvoice.subtotal))
              ),
              selectedInvoice.invoice_discount_amount > 0 && React.createElement('div', { className: 'flex justify-between text-red-600' },
                React.createElement('span', null, 'Discount:'),
                React.createElement('span', null, '-' + formatCurrency(selectedInvoice.invoice_discount_amount))
              ),
              React.createElement('div', { className: 'flex justify-between' },
                React.createElement('span', null, 'CGST:'),
                React.createElement('span', null, formatCurrency(selectedInvoice.cgst))
              ),
              React.createElement('div', { className: 'flex justify-between' },
                React.createElement('span', null, 'SGST:'),
                React.createElement('span', null, formatCurrency(selectedInvoice.sgst))
              ),
              selectedInvoice.igst > 0 && React.createElement('div', { className: 'flex justify-between' },
                React.createElement('span', null, 'IGST:'),
                React.createElement('span', null, formatCurrency(selectedInvoice.igst))
              ),
              selectedInvoice.round_off !== 0 && React.createElement('div', { className: 'flex justify-between' },
                React.createElement('span', null, 'Round Off:'),
                React.createElement('span', null, formatCurrency(selectedInvoice.round_off))
              ),
              React.createElement('div', { className: 'flex justify-between font-bold text-lg border-t pt-2' },
                React.createElement('span', null, 'Grand Total:'),
                React.createElement('span', null, formatCurrency(selectedInvoice.grand_total))
              ),
              React.createElement('div', { className: 'flex justify-between text-green-600' },
                React.createElement('span', null, 'Paid:'),
                React.createElement('span', null, formatCurrency(selectedInvoice.amount_paid))
              ),
              React.createElement('div', { className: 'flex justify-between font-semibold ' + (selectedInvoice.balance_due > 0 ? 'text-amber-600' : 'text-green-600') },
                React.createElement('span', null, 'Balance Due:'),
                React.createElement('span', null, formatCurrency(selectedInvoice.balance_due))
              )
            )
          ),
          
          // Actions
          React.createElement('div', { className: 'flex justify-end gap-2 pt-4 border-t flex-wrap' },
            selectedInvoice.status === 'draft' && canEditInvoice && React.createElement(Button, {
              onClick: function() { finalizeInvoice(selectedInvoice.id); },
              className: 'bg-blue-600 hover:bg-blue-700'
            }, 'Finalize Invoice'),
            selectedInvoice.status !== 'draft' && selectedInvoice.status !== 'cancelled' && React.createElement(Button, {
              variant: 'outline',
              className: 'gap-1 border-green-300 text-green-700 hover:bg-green-50',
              onClick: function() { openEwayBill(selectedInvoice); },
              'data-testid': 'eway-bill-btn'
            },
              React.createElement(Truck, { className: 'w-4 h-4' }),
              selectedInvoice.eway_bill_number ? 'E-way Bill: ' + selectedInvoice.eway_bill_number : 'Generate E-way Bill'
            ),
            selectedInvoice.status !== 'cancelled' && selectedInvoice.status !== 'paid' && React.createElement(Button, {
              variant: 'outline',
              onClick: function() { window.location.href = '/invoices/' + selectedInvoice.id + '/payment'; }
            }, 'Record Payment'),
            selectedInvoice.status !== 'cancelled' && selectedInvoice.status !== 'paid' && canDeleteInvoice && React.createElement(Button, {
              variant: 'outline',
              className: 'text-red-600 border-red-300 hover:bg-red-50',
              onClick: function() { cancelInvoice(selectedInvoice.id); }
            }, 'Cancel Invoice'),
            React.createElement(Button, {
              variant: 'outline',
              onClick: function() { downloadPDF(selectedInvoice.id, selectedInvoice.invoice_number); }
            },
              React.createElement(Download, { className: 'w-4 h-4 mr-2' }),
              'Download PDF'
            )
          )
        )
      )
    )
      ), // End TabsContent dashboard
      
      // Bulk Upload Tab Content
      React.createElement(TabsContent, { value: 'bulk-upload', className: 'mt-4' },
        React.createElement(BulkInvoiceUpload, null)
      )
    ), // End Tabs

    // E-way Bill Dialog
    React.createElement(EwayBillDialog, {
      open: ewayDialogOpen,
      onOpenChange: function(open) {
        setEwayDialogOpen(open);
        if (!open) { setEwayAutoPrompt(false); }
      },
      invoice: ewayInvoice,
      autoPrompt: ewayAutoPrompt,
      onSuccess: function() {
        fetchInvoices();
        setEwayDialogOpen(false);
        setEwayAutoPrompt(false);
      }
    })
  );
}

export default InvoiceDashboard;
